/**
 * API Gateway Service for Tic-Tac-Toe Game
 * Handles REST API calls to AWS Lambda functions
 */

import { 
  GameSession, 
  Profile, 
  RoomCode, 
  GameStatistics, 
  GameResult,
  LobbyGame,
  Move,
  GameId,
  PlayerId 
} from '../types';
import { AWS_CONFIG } from './config';

/**
 * Base API client for making requests to API Gateway
 */
class APIClient {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor() {
    this.baseUrl = AWS_CONFIG.apiGateway.baseUrl;
  }

  /**
   * Set authentication token for API requests
   */
  setAuthToken(token: string) {
    this.authToken = token;
  }

  /**
   * Make authenticated API request
   */
  private async makeRequest<T>(
    endpoint: string, 
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
    body?: any
  ): Promise<T | null> {
    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (this.authToken) {
        headers['Authorization'] = `Bearer ${this.authToken}`;
      }

      const config: RequestInit = {
        method,
        headers,
      };

      if (body && method !== 'GET') {
        config.body = JSON.stringify(body);
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, config);

      if (!response.ok) {
        console.error(`API request failed: ${response.status} ${response.statusText}`);
        return null;
      }

      const data = await response.json();
      return data as T;
    } catch (error) {
      console.error('API request error:', error);
      return null;
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string): Promise<T | null> {
    return this.makeRequest<T>(endpoint, 'GET');
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: any): Promise<T | null> {
    return this.makeRequest<T>(endpoint, 'POST', body);
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: any): Promise<T | null> {
    return this.makeRequest<T>(endpoint, 'PUT', body);
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T | null> {
    return this.makeRequest<T>(endpoint, 'DELETE');
  }
}

// Global API client instance
const apiClient = new APIClient();

/**
 * Game Logic API Service
 */
export class GameLogicAPI {
  /**
   * Create a new game session
   */
  static async createGameSession(gameData: Partial<GameSession>): Promise<GameSession | null> {
    return apiClient.post<GameSession>('/games', gameData);
  }

  /**
   * Get game session by ID
   */
  static async getGameSession(gameId: GameId): Promise<GameSession | null> {
    return apiClient.get<GameSession>(`/games/${gameId}`);
  }

  /**
   * Make a move in a game
   */
  static async makeMove(gameId: GameId, move: Move): Promise<GameSession | null> {
    return apiClient.post<GameSession>(`/games/${gameId}/moves`, move);
  }

  /**
   * Get game state
   */
  static async getGameState(gameId: GameId): Promise<GameSession | null> {
    return apiClient.get<GameSession>(`/games/${gameId}/state`);
  }

  /**
   * End a game session
   */
  static async endGameSession(gameId: GameId): Promise<boolean> {
    const result = await apiClient.delete(`/games/${gameId}`);
    return result !== null;
  }
}

/**
 * User Management API Service
 */
export class UserManagementAPI {
  /**
   * Create a new user profile
   */
  static async createUserProfile(profileData: Partial<Profile>): Promise<Profile | null> {
    return apiClient.post<Profile>('/users', profileData);
  }

  /**
   * Get user profile by ID
   */
  static async getUserProfile(userId: string): Promise<Profile | null> {
    return apiClient.get<Profile>(`/users/${userId}`);
  }

  /**
   * Update user profile
   */
  static async updateUserProfile(userId: string, profileData: Partial<Profile>): Promise<Profile | null> {
    return apiClient.put<Profile>(`/users/${userId}`, profileData);
  }

  /**
   * Get user statistics
   */
  static async getUserStatistics(userId: string): Promise<GameStatistics | null> {
    return apiClient.get<GameStatistics>(`/users/${userId}/statistics`);
  }

  /**
   * Record game result
   */
  static async recordGameResult(userId: string, gameResult: GameResult): Promise<boolean> {
    const result = await apiClient.post(`/users/${userId}/games`, gameResult);
    return result !== null;
  }

  /**
   * Sync user data across devices
   */
  static async syncUserData(userId: string, localData: any): Promise<Profile | null> {
    return apiClient.post<Profile>(`/users/${userId}/sync`, localData);
  }
}

/**
 * Multiplayer Manager API Service
 */
export class MultiplayerAPI {
  /**
   * Create a new room code
   */
  static async createRoomCode(): Promise<RoomCode | null> {
    return apiClient.post<RoomCode>('/rooms');
  }

  /**
   * Validate and get room code
   */
  static async validateRoomCode(code: string): Promise<RoomCode | null> {
    return apiClient.get<RoomCode>(`/rooms/${code}`);
  }

  /**
   * Join a room using room code
   */
  static async joinRoom(code: string, playerId: PlayerId): Promise<GameSession | null> {
    return apiClient.post<GameSession>(`/rooms/${code}/join`, { playerId });
  }

  /**
   * Get lobby games
   */
  static async getLobbyGames(): Promise<LobbyGame[]> {
    const result = await apiClient.get<{ games: LobbyGame[] }>('/lobby');
    return result?.games || [];
  }

  /**
   * Create lobby game
   */
  static async createLobbyGame(gameData: Partial<LobbyGame>): Promise<LobbyGame | null> {
    return apiClient.post<LobbyGame>('/lobby', gameData);
  }

  /**
   * Join lobby game
   */
  static async joinLobbyGame(gameId: GameId, playerId: PlayerId): Promise<GameSession | null> {
    return apiClient.post<GameSession>(`/lobby/${gameId}/join`, { playerId });
  }

  /**
   * Leave a multiplayer game
   */
  static async leaveGame(gameId: GameId, playerId: PlayerId): Promise<boolean> {
    const result = await apiClient.post(`/games/${gameId}/leave`, { playerId });
    return result !== null;
  }

  /**
   * Update game settings
   */
  static async updateGameSettings(gameId: GameId, settings: any): Promise<boolean> {
    const result = await apiClient.put(`/games/${gameId}/settings`, settings);
    return result !== null;
  }

  /**
   * Send turn notification
   */
  static async sendTurnNotification(gameId: GameId, playerId: PlayerId): Promise<boolean> {
    const result = await apiClient.post(`/games/${gameId}/notifications`, { 
      playerId, 
      type: 'turn',
      message: 'It\'s your turn!'
    });
    return result !== null;
  }

  /**
   * Forfeit turn due to timer expiration
   */
  static async forfeitTurn(gameId: GameId, playerId: PlayerId): Promise<boolean> {
    const result = await apiClient.post(`/games/${gameId}/forfeit`, { playerId });
    return result !== null;
  }

  /**
   * Get current game state
   */
  static async getGameState(gameId: GameId): Promise<any | null> {
    return apiClient.get(`/games/${gameId}/state`);
  }

  /**
   * Update game state
   */
  static async updateGameState(gameId: GameId, gameState: any): Promise<boolean> {
    const result = await apiClient.put(`/games/${gameId}/state`, gameState);
    return result !== null;
  }
}

/**
 * Statistics Manager API Service
 */
export class StatisticsAPI {
  /**
   * Get global leaderboard
   */
  static async getGlobalLeaderboard(gameMode?: string, difficulty?: string): Promise<any[]> {
    let endpoint = '/leaderboard';
    const params = new URLSearchParams();
    
    if (gameMode) params.append('mode', gameMode);
    if (difficulty) params.append('difficulty', difficulty);
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    const result = await apiClient.get<{ leaderboard: any[] }>(endpoint);
    return result?.leaderboard || [];
  }

  /**
   * Get user ranking
   */
  static async getUserRanking(userId: string, gameMode?: string, difficulty?: string): Promise<number | null> {
    let endpoint = `/users/${userId}/ranking`;
    const params = new URLSearchParams();
    
    if (gameMode) params.append('mode', gameMode);
    if (difficulty) params.append('difficulty', difficulty);
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    const result = await apiClient.get<{ ranking: number }>(endpoint);
    return result?.ranking || null;
  }

  /**
   * Get game history for user
   */
  static async getGameHistory(userId: string, limit?: number, offset?: number): Promise<GameResult[]> {
    let endpoint = `/users/${userId}/history`;
    const params = new URLSearchParams();
    
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
    
    const result = await apiClient.get<{ history: GameResult[] }>(endpoint);
    return result?.history || [];
  }
}

/**
 * Set authentication token for all API calls
 */
export const setAPIAuthToken = (token: string) => {
  apiClient.setAuthToken(token);
};

/**
 * Clear authentication token
 */
export const clearAPIAuthToken = () => {
  apiClient.setAuthToken('');
};